// Shrink a photo in the browser before it goes up, so Smartsheet keeps a sensible copy (a phone photo is
// 3-8 MB; at 1920px on the long edge and JPEG 0.82 it is a few hundred KB and still sharp on any screen).
// Decoding uses createImageBitmap (EXIF orientation applied), falling back to an <img> fed a data: URL
// (the app's CSP allows data: images, not blob:). A HEIC / HEIF photo (an iPhone's) that the browser can't
// read itself (Chrome, Edge, Firefox on a desktop) is read by lib/heic.ts instead, and always goes up as a
// JPEG, so every browser can show it. A photo nothing could read goes up as it is when it is within the
// upload limit; otherwise it is refused with a clear message.
import { SERVICE_PHOTO_MAX_BYTES, isServicePhotoType } from '@technohull/shared';
import { decodeHeic, heicCanvas, isHeicName, isHeicType, sniffHeic } from './heic.js';

export const SHRINK_MAX_EDGE = 1920;
export const SHRINK_QUALITY = 0.82;
/** a photo that needs no scaling is only re-encoded when that saves at least this share of its size */
const WORTH_IT = 0.9;

/** a photo the app won't take, with a message fit to show. */
export class PhotoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PhotoError';
  }
}

/** the size to draw at: the long edge capped at `maxEdge`, the aspect kept, whole pixels, never upscaled. */
export function fitWithin(width: number, height: number, maxEdge = SHRINK_MAX_EDGE): { width: number; height: number; scaled: boolean } {
  if (!(width > 0) || !(height > 0) || !Number.isFinite(width) || !Number.isFinite(height)) return { width: 0, height: 0, scaled: false };
  const long = Math.max(width, height);
  if (long <= maxEdge) return { width: Math.round(width), height: Math.round(height), scaled: false };
  const k = maxEdge / long;
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)), scaled: true };
}

/** the file's name with a .jpg extension ("IMG_0042.HEIC" → "IMG_0042.jpg"). */
export function jpegName(name: string): string {
  const base = name.replace(/\.[^./\\]{1,6}$/, '').trim();
  return `${base || 'photo'}.jpg`;
}

const EXT_TYPE: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif',
  heic: 'image/heic', heif: 'image/heif', avif: 'image/avif',
};
/** a file's image type; some pickers leave `type` empty (or call a HEIC on Windows just "binary data"), so
 *  the extension decides then. */
export function imageTypeOf(file: { type: string; name: string }): string {
  const t = file.type.trim().toLowerCase();
  if (t && t !== 'application/octet-stream') return t === 'image/jpg' ? 'image/jpeg' : t;
  const ext = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase() ?? '';
  return EXT_TYPE[ext] ?? '';
}

const mb = (n: number) => `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
const LIMIT = `Photos can be up to ${SERVICE_PHOTO_MAX_BYTES / 1024 / 1024} MB`;

export interface DecodedImage {
  width: number;
  height: number;
  source: CanvasImageSource;
  close?: () => void;
  /** read by the HEIC reader: the photo is a HEIC, whatever its name or type said */
  heic?: boolean;
}
export interface ShrunkPhoto { blob: Blob; name: string; type: string; shrunk: boolean }
export interface ShrinkDeps {
  decode?: (file: Blob) => Promise<DecodedImage>;
  encode?: (img: DecodedImage, width: number, height: number, quality: number) => Promise<Blob | null>;
}

/** a photo ready to upload: scaled down and re-encoded as JPEG when that helps, else the original. */
export async function shrinkImage(file: File, deps: ShrinkDeps = {}): Promise<ShrunkPhoto> {
  const type = imageTypeOf(file);
  if (!isServicePhotoType(type)) {
    throw new PhotoError(type.startsWith('image/') ? 'This kind of image can’t be added. Use a JPEG, PNG, WebP or HEIC photo.' : 'Only photos can be added.');
  }
  if (!file.size) throw new PhotoError('This photo is empty.');
  const original = (): ShrunkPhoto => {
    if (file.size > SERVICE_PHOTO_MAX_BYTES) throw new PhotoError(`This photo is ${mb(file.size)} and couldn’t be made smaller here. ${LIMIT}.`);
    return { blob: file, name: file.name || `photo.${type.split('/')[1]}`, type, shrunk: false };
  };
  let img: DecodedImage;
  try {
    img = await (deps.decode ?? decodeImage)(file);
  } catch {
    return original();
  }
  try {
    const fit = fitWithin(img.width, img.height);
    if (!fit.width) return original();
    let out: Blob | null = null;
    try {
      out = await (deps.encode ?? encodeJpeg)(img, fit.width, fit.height, SHRINK_QUALITY);
    } catch {
      out = null;
    }
    if (!out?.size) return original();
    // already small enough: re-encoding would only cost quality unless it really saves space. A HEIC is
    // always made a JPEG: most browsers can't show one.
    const heic = !!img.heic || isHeicType(type) || isHeicName(file.name);
    if (!heic && !fit.scaled && file.size <= SERVICE_PHOTO_MAX_BYTES && out.size > file.size * WORTH_IT) return original();
    if (out.size > SERVICE_PHOTO_MAX_BYTES) throw new PhotoError(`This photo is still ${mb(out.size)} after shrinking. ${LIMIT}.`);
    return { blob: out, name: jpegName(file.name), type: 'image/jpeg', shrunk: true };
  } finally {
    img.close?.();
  }
}

/** a data: URL of a blob, e.g. for a preview while it uploads. */
export function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('Could not read the file'));
    r.readAsDataURL(blob);
  });
}

/** decode an image (EXIF orientation applied); throws when the browser can't. A HEIC the browser can't
 *  read itself is read by the HEIC reader (lib/heic.ts), by its type, name or first bytes. */
export async function decodeImage(file: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { width: bmp.width, height: bmp.height, source: bmp, close: () => bmp.close() };
    } catch {
      /* an older engine or an option it doesn't know, or a HEIC: try the HEIC reader, then an <img> */
    }
  }
  const name = file instanceof File ? file.name : '';
  if (isHeicType(file.type) || isHeicName(name) || (await sniffHeic(file))) {
    const canvas = heicCanvas(await decodeHeic(file));
    return { width: canvas.width, height: canvas.height, source: canvas, heic: true, close: () => { canvas.width = 0; canvas.height = 0; } };
  }
  const src = await readAsDataUrl(file);
  const el = new Image();
  el.decoding = 'async';
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('decode timed out')), 20_000);
    el.onload = () => { clearTimeout(timer); resolve(); };
    el.onerror = () => { clearTimeout(timer); reject(new Error('decode failed')); };
    el.src = src;
  });
  if (!el.naturalWidth || !el.naturalHeight) throw new Error('decode failed');
  return { width: el.naturalWidth, height: el.naturalHeight, source: el };
}

/** draw a decoded image at a size and encode it as a JPEG (on white, so transparency doesn't turn black). */
export async function encodeJpeg(img: DecodedImage, width: number, height: number, quality: number): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const g = canvas.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#ffffff'; // a transparent PNG would otherwise turn black in a JPEG
  g.fillRect(0, 0, width, height);
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  g.drawImage(img.source, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  canvas.width = 0; // hand the bitmap memory back straight away (iOS keeps a tight canvas budget)
  canvas.height = 0;
  return blob;
}
